/**
 * Panel scaling utilities for proportional plot scaling in multi-panel layouts
 */

// Preview DPI for on-screen rendering (lower for performance)
export const PREVIEW_DPI = 150;

// Export DPI for final output (higher for quality)
export const EXPORT_DPI = 300;

/**
 * Convert millimeters to pixels at a given DPI
 */
export function mmToPxAtDpi(mm: number, dpi: number): number {
  return (mm / 25.4) * dpi;
}

/**
 * Convert pixels to millimeters at a given DPI
 */
export function pxToMmAtDpi(px: number, dpi: number): number {
  return (px / dpi) * 25.4;
}

/**
 * Canonical base figure size in mm (single-panel full figure size)
 * This is the size a plot would normally render at when displayed alone
 */
export const BASE_FIGURE_WIDTH_MM = 180;
export const BASE_FIGURE_HEIGHT_MM = 120;

/**
 * Calculate scale factor for a panel based on its size vs base figure size
 * Uses the minimum of width/height ratios to maintain aspect ratio
 */
export function calculateScaleFactor(
  panelWidthMm: number,
  panelHeightMm: number
): number {
  const widthScale = panelWidthMm / BASE_FIGURE_WIDTH_MM;
  const heightScale = panelHeightMm / BASE_FIGURE_HEIGHT_MM;
  return Math.min(widthScale, heightScale);
}

/**
 * Calculate panel dimensions in mm from grid units
 */
export function calculatePanelSizeMm(
  panelW: number, // grid units (columns)
  panelH: number, // grid units (rows)
  canvasWidthMm: number,
  canvasHeightMm: number,
  gridCols: number,
  rowHeightPx: number,
  margin: [number, number],
  dpi: number = PREVIEW_DPI
): { widthMm: number; heightMm: number } {
  // Calculate column width in pixels
  const colWidthPx = mmToPxAtDpi(canvasWidthMm, dpi) / gridCols;
  
  // Panel width in pixels = (columns * colWidth) + (gaps * margin)
  const panelWidthPx = panelW * colWidthPx + (panelW - 1) * margin[0];
  
  // Panel height in pixels = (rows * rowHeight) + (gaps * margin)
  const panelHeightPx = panelH * rowHeightPx + (panelH - 1) * margin[1];
  
  // Convert back to mm
  const widthMm = pxToMmAtDpi(panelWidthPx, dpi);
  const heightMm = pxToMmAtDpi(panelHeightPx, dpi);
  
  return { widthMm, heightMm };
}

/**
 * Scale Plotly layout properties (margins, fonts) based on scale factor
 */
export function scalePlotlyLayout(
  baseLayout: any,
  scaleFactor: number
): any {
  const scaledLayout = { ...baseLayout };
  
  // Scale margins
  if (scaledLayout.margin) {
    scaledLayout.margin = {
      l: (scaledLayout.margin.l || 0) * scaleFactor,
      r: (scaledLayout.margin.r || 0) * scaleFactor,
      t: (scaledLayout.margin.t || 0) * scaleFactor,
      b: (scaledLayout.margin.b || 0) * scaleFactor,
    };
  }
  
  // Scale font sizes
  if (scaledLayout.font) {
    scaledLayout.font = {
      ...scaledLayout.font,
      size: (scaledLayout.font.size || 12) * scaleFactor,
    };
  }
  
  // Scale plot title font
  if (scaledLayout.title) {
    if (typeof scaledLayout.title === 'object' && scaledLayout.title.font) {
      scaledLayout.title = {
        ...scaledLayout.title,
        font: {
          ...scaledLayout.title.font,
          size: (scaledLayout.title.font.size || 16) * scaleFactor,
        },
      };
    }
    // Also handle subtitle font if present
    if (scaledLayout.title.subtitle && typeof scaledLayout.title.subtitle === 'object' && scaledLayout.title.subtitle.font) {
      scaledLayout.title.subtitle = {
        ...scaledLayout.title.subtitle,
        font: {
          ...scaledLayout.title.subtitle.font,
          size: (scaledLayout.title.subtitle.font.size || 14) * scaleFactor,
        },
      };
    }
  }
  
  // Scale axis title fonts
  if (scaledLayout.xaxis?.title?.font) {
    scaledLayout.xaxis.title.font = {
      ...scaledLayout.xaxis.title.font,
      size: (scaledLayout.xaxis.title.font.size || 12) * scaleFactor,
    };
  }
  
  if (scaledLayout.yaxis?.title?.font) {
    scaledLayout.yaxis.title.font = {
      ...scaledLayout.yaxis.title.font,
      size: (scaledLayout.yaxis.title.font.size || 12) * scaleFactor,
    };
  }
  
  // Scale tick fonts
  if (scaledLayout.xaxis?.tickfont) {
    scaledLayout.xaxis.tickfont = {
      ...scaledLayout.xaxis.tickfont,
      size: (scaledLayout.xaxis.tickfont.size || 12) * scaleFactor,
    };
  }
  
  if (scaledLayout.yaxis?.tickfont) {
    scaledLayout.yaxis.tickfont = {
      ...scaledLayout.yaxis.tickfont,
      size: (scaledLayout.yaxis.tickfont.size || 12) * scaleFactor,
    };
  }
  
  // Scale secondary axes (xaxis2, yaxis2, etc.) - handle up to xaxis10/yaxis10
  for (let i = 2; i <= 10; i++) {
    const xAxisKey = `xaxis${i}` as keyof typeof scaledLayout;
    const yAxisKey = `yaxis${i}` as keyof typeof scaledLayout;
    
    if (scaledLayout[xAxisKey]) {
      const axis = scaledLayout[xAxisKey] as any;
      if (axis?.title?.font) {
        axis.title.font = {
          ...axis.title.font,
          size: (axis.title.font.size || 12) * scaleFactor,
        };
      }
      if (axis?.tickfont) {
        axis.tickfont = {
          ...axis.tickfont,
          size: (axis.tickfont.size || 12) * scaleFactor,
        };
      }
    }
    
    if (scaledLayout[yAxisKey]) {
      const axis = scaledLayout[yAxisKey] as any;
      if (axis?.title?.font) {
        axis.title.font = {
          ...axis.title.font,
          size: (axis.title.font.size || 12) * scaleFactor,
        };
      }
      if (axis?.tickfont) {
        axis.tickfont = {
          ...axis.tickfont,
          size: (axis.tickfont.size || 12) * scaleFactor,
        };
      }
    }
  }
  
  // Scale colorbar fonts (for heatmaps, contour plots, etc.)
  if (scaledLayout.coloraxis?.colorbar) {
    const colorbar = scaledLayout.coloraxis.colorbar;
    if (colorbar.title?.font) {
      colorbar.title = {
        ...colorbar.title,
        font: {
          ...colorbar.title.font,
          size: (colorbar.title.font.size || 12) * scaleFactor,
        },
      };
    }
    if (colorbar.tickfont) {
      colorbar.tickfont = {
        ...colorbar.tickfont,
        size: (colorbar.tickfont.size || 12) * scaleFactor,
      };
    }
  }
  
  // Also check for colorbar in individual traces (layout.colorbar)
  if (scaledLayout.colorbar) {
    const colorbar = scaledLayout.colorbar;
    if (colorbar.title?.font) {
      colorbar.title = {
        ...colorbar.title,
        font: {
          ...colorbar.title.font,
          size: (colorbar.title.font.size || 12) * scaleFactor,
        },
      };
    }
    if (colorbar.tickfont) {
      colorbar.tickfont = {
        ...colorbar.tickfont,
        size: (colorbar.tickfont.size || 12) * scaleFactor,
      };
    }
  }
  
  // Scale legend properties comprehensively
  if (scaledLayout.legend) {
    scaledLayout.legend = { ...scaledLayout.legend };
    
    // Scale legend font size
    if (scaledLayout.legend.font) {
      scaledLayout.legend.font = {
        ...scaledLayout.legend.font,
        size: (scaledLayout.legend.font.size || 12) * scaleFactor,
      };
    }
    
    // Scale legend title font size
    if (scaledLayout.legend.title?.font) {
      scaledLayout.legend.title = {
        ...scaledLayout.legend.title,
        font: {
          ...scaledLayout.legend.title.font,
          size: (scaledLayout.legend.title.font.size || 12) * scaleFactor,
        },
      };
    }
    
    // Scale legend border width
    if (typeof scaledLayout.legend.borderwidth === 'number') {
      scaledLayout.legend.borderwidth = scaledLayout.legend.borderwidth * scaleFactor;
    }
    
    // Scale legend item width/height (affects marker size in legend)
    if (typeof scaledLayout.legend.itemwidth === 'number') {
      scaledLayout.legend.itemwidth = scaledLayout.legend.itemwidth * scaleFactor;
    }
    if (typeof scaledLayout.legend.itemheight === 'number') {
      scaledLayout.legend.itemheight = scaledLayout.legend.itemheight * scaleFactor;
    }
    
    // Scale legend spacing (gap between items)
    if (typeof scaledLayout.legend.tracegroupgap === 'number') {
      scaledLayout.legend.tracegroupgap = scaledLayout.legend.tracegroupgap * scaleFactor;
    }
    
    // Scale legend padding (x and y padding)
    if (typeof scaledLayout.legend.x === 'number') {
      // x/y are positions, but we might want to scale them relative to plot size
      // Actually, x/y are normalized coordinates (0-1), so we shouldn't scale them
      // But if they're in pixels (which is rare), we'd scale them
      // For now, leave x/y as-is since they're typically normalized
    }
  }
  
  return scaledLayout;
}

/**
 * Calculate effective font size in points at print
 */
export function calculateFontSizePt(fontSizePx: number, dpi: number): number {
  const ptPerPixel = 72 / dpi;
  return fontSizePx * ptPerPixel;
}


